import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@loot-os/ui/components/alert-dialog";
import { Button } from "@loot-os/ui/components/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@loot-os/ui/components/dropdown-menu";
import { toast } from "@loot-os/ui/components/sonner";
import { Archive, Copy, Ellipsis, RefreshCw, Trash2 } from "lucide-react";
import { useState } from "react";

import type { BoxEditorData } from "../lib/boxEditor";
import type { BoxEditorWrites } from "./BoxEditorLayout";

/**
 * The box's less frequent actions: copy it, refresh the published version now, archive it and
 * delete it. Delete is on only when nothing references the box (`deleteBlocker`, from the
 * server), so the menu never offers a delete the server would refuse. A box whose table is
 * generated (a Shopify rule box) has no Refresh: the server rebuilds it from its rules.
 */
export function BoxActionsMenu({
  box,
  writes,
  archiveConfirm,
  generated = false,
}: {
  box: BoxEditorData;
  writes: BoxEditorWrites;
  archiveConfirm?: { title: string; description: string };
  generated?: boolean;
}) {
  const [confirm, setConfirm] = useState<"delete" | "archive" | null>(null);
  const canDelete = box.deleteBlocker === null;
  const archived = box.status === "archived";

  const archive = () => {
    writes.update.submit({ status: "archived" });
    setConfirm(null);
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger
          render={<Button variant="outline" size="icon" aria-label="More box actions" />}
        >
          <Ellipsis />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-40">
          <DropdownMenuItem
            disabled={writes.duplicate.isPending}
            onClick={() => writes.duplicate.submit({})}
          >
            <Copy />
            Duplicate
          </DropdownMenuItem>
          {box.currentVersion > 0 && !generated && (
            <DropdownMenuItem
              disabled={writes.refresh.isPending}
              onClick={() => writes.refresh.submit({})}
            >
              <RefreshCw />
              Refresh now
            </DropdownMenuItem>
          )}
          <DropdownMenuSeparator />
          {!archived && (
            <DropdownMenuItem onClick={() => (archiveConfirm ? setConfirm("archive") : archive())}>
              <Archive />
              Archive
            </DropdownMenuItem>
          )}
          <DropdownMenuItem
            variant="destructive"
            disabled={!canDelete || writes.remove.isPending}
            onClick={() => setConfirm("delete")}
          >
            <Trash2 />
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <AlertDialog open={confirm !== null} onOpenChange={(open) => !open && setConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirm === "delete" ? `Delete "${box.name}"?` : archiveConfirm?.title}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirm === "delete"
                ? "Nothing has opened this box, so it can be deleted outright: its versions go with it, a Shopify product selling it is deleted from the store, and this cannot be undone."
                : archiveConfirm?.description}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (confirm === "archive") return archive();
                writes.remove.submit(
                  {},
                  { onSuccess: () => toast.success(`Deleted "${box.name}"`) },
                );
                setConfirm(null);
              }}
            >
              {confirm === "delete" ? "Delete box" : "Archive box"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
